import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import { useSearchParams } from 'react-router-dom';
import { YPageShell, YPageShellBody } from '../../../ui/shell/YPageShell';
import { YButton } from '../../../ui/YButton';
import { PlaceSearchInput, CITY_TYPES } from '../../../components/ui/PlaceSearchInput';
import { CommunityShellTabs } from '../components/CommunityShellTabs';
import { ProcessorRow } from '../../processors/components/ProcessorRow';
import { ProcessorMapView } from '../../processors/components/ProcessorMapView';
import { ProcessorDetailPanel } from '../../processors/components/ProcessorDetailPanel';
import { AddProcessorPanel } from '../../processors/components/AddProcessorPanel';
import { fetchProcessor, searchProcessors } from '../../processors/services/processorService';
import {
  filterAndSortProcessors,
  isProcessorTraceEnabled,
  PROCESSOR_SOURCE_ALL,
  PROCESSOR_SOURCE_PLACE,
  PROCESSOR_SOURCE_YONA,
} from '../../processors/utils/processorList';
import { formatProcessorTrace } from '../../processors/utils/processorSearch';
import { readProcessorUrlState, writeProcessorUrlState } from '../../processors/utils/processorUrl';
import './find-a-processor-page.css';

const RADIUS_MIN = 10;
const RADIUS_MAX = 90;
const DEFAULT_RADIUS = 40;

const SOURCE_PILLS = [
  { value: PROCESSOR_SOURCE_ALL, label: 'ALL' },
  { value: PROCESSOR_SOURCE_YONA, label: 'YONA DIRECTORY' },
  { value: PROCESSOR_SOURCE_PLACE, label: 'NEARBY PLACES' },
];

const EMPTY_BEFORE = {
  title: 'Run a search',
  body: 'Pick a town and hit search. We sweep map data for butchers and processors, then merge in listings Yona members have added.',
};

const EMPTY_AFTER = {
  title: 'Nothing in range',
  body: 'Widen the radius, or add one you know about so the next hunter finds it.',
};

function traceHasError(trace) {
  return Boolean(trace?.category?.error || trace?.deerProcessing?.error || trace?.meatProcessing?.error);
}

export function FindAProcessorPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlState = readProcessorUrlState(searchParams);
  const [placeQuery, setPlaceQuery] = useState('');
  const [selectedPlace, setSelectedPlace] = useState(null);
  const [radius, setRadius] = useState(DEFAULT_RADIUS);
  const [source, setSource] = useState(PROCESSOR_SOURCE_ALL);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [mergedRows, setMergedRows] = useState([]);
  const [trace, setTrace] = useState(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [activeId, setActiveId] = useState(urlState.processorId);
  const [addOpen, setAddOpen] = useState(urlState.add);
  const rowRefs = useRef({});
  const hydratedRef = useRef(false);

  const applyUrlState = useCallback((patch) => {
    setSearchParams(writeProcessorUrlState(searchParams, patch), { replace: true });
  }, [searchParams, setSearchParams]);

  const handleSelect = useCallback((id) => {
    setActiveId(id);
    applyUrlState({ processorId: id, add: false, fromId: null });
    const node = rowRefs.current[id];
    if (node?.scrollIntoView) {
      node.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [applyUrlState]);

  const closeDetail = useCallback(() => {
    setActiveId(null);
    applyUrlState({ processorId: null });
  }, [applyUrlState]);

  const closeAdd = useCallback(() => {
    setAddOpen(false);
    applyUrlState({ add: false, fromId: null });
  }, [applyUrlState]);

  const openAdd = useCallback((fromRow = null) => {
    setAddOpen(true);
    setActiveId(null);
    applyUrlState({
      add: true,
      fromId: fromRow?.id || null,
      processorId: null,
    });
  }, [applyUrlState]);

  const rows = useMemo(
    () =>
      filterAndSortProcessors({
        rows: mergedRows,
        origin: selectedPlace,
        radiusMiles: radius,
        source,
      }),
    [mergedRows, radius, selectedPlace, source]
  );

  const activeRow = useMemo(() => {
    if (!activeId) return null;
    return rows.find((row) => String(row.id) === String(activeId))
      || mergedRows.find((row) => String(row.id) === String(activeId))
      || null;
  }, [activeId, mergedRows, rows]);

  const addSeed = useMemo(() => {
    if (!urlState.fromId) return null;
    return rows.find((row) => String(row.id) === String(urlState.fromId))
      || mergedRows.find((row) => String(row.id) === String(urlState.fromId))
      || null;
  }, [mergedRows, rows, urlState.fromId]);

  const placeLabel = useMemo(() => {
    const raw = selectedPlace?.label || selectedPlace?.name || placeQuery || 'NOWHERE';
    return String(raw).toUpperCase();
  }, [placeQuery, selectedPlace]);

  const runSearch = useCallback(async (originPlace, options = {}) => {
    const place = originPlace || selectedPlace;
    if (!place) {
      setError('Select a city or town to search near.');
      return;
    }
    setError('');
    setLoading(true);
    try {
      const { rows, trace: searchTrace } = await searchProcessors({
        latitude: place.latitude,
        longitude: place.longitude,
        radiusMiles: radius,
        source,
      });
      setMergedRows(Array.isArray(rows) ? rows : []);
      setTrace(searchTrace || null);
      setHasSearched(true);
      if (options.activeId) {
        setActiveId(options.activeId);
      } else {
        setActiveId(null);
        applyUrlState({ processorId: null });
      }
      if (traceHasError(searchTrace)) {
        setError('Map search partly failed — showing what came back.');
      }
    } catch (_) {
      setMergedRows([]);
      setTrace(null);
      setError('Unable to load processors right now. Try again in a moment.');
    } finally {
      setLoading(false);
    }
  }, [applyUrlState, radius, selectedPlace, source]);

  useEffect(() => {
    if (hydratedRef.current) return undefined;
    hydratedRef.current = true;
    const { processorId, add } = readProcessorUrlState(searchParams);
    setAddOpen(add);
    if (!processorId) return undefined;
    let cancelled = false;
    (async () => {
      const row = await fetchProcessor(processorId);
      if (cancelled) return;
      if (!row || !Number.isFinite(row.latitude) || !Number.isFinite(row.longitude)) {
        setActiveId(processorId);
        return;
      }
      const origin = {
        latitude: row.latitude,
        longitude: row.longitude,
        label: row.town || row.name,
      };
      setSelectedPlace(origin);
      setPlaceQuery(origin.label || '');
      await runSearch(origin, { activeId: processorId });
    })();
    return () => {
      cancelled = true;
    };
    // Deep-link hydrate once from the landing URL.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = (event) => {
    event.preventDefault();
    runSearch();
  };

  const empty = hasSearched ? EMPTY_AFTER : EMPTY_BEFORE;
  const showTrace = Boolean(trace && isProcessorTraceEnabled());
  const headingCount = hasSearched ? rows.length : 0;

  return (
    <YPageShell testId="find-a-processor-page" className="fap-page fap-page--field">
      <CommunityShellTabs />
      <YPageShellBody className="fap-body">
        <div
          className="fap-layout"
          data-processor-id={urlState.processorId || ''}
          data-testid="fap-url-state"
        >
          <div className="fap-col">
            <section className="fap-search-panel" aria-labelledby="fap-search-heading">
              <h2 id="fap-search-heading" className="fap-search-panel__title">
                Search by location
              </h2>
              <p className="fap-search-panel__intro">
                Results come from map data and from listings Yona members have added. Hours
                move around all season, so call before you drive.
              </p>

              <form className="fap-search-form" onSubmit={handleSubmit}>
                <label className="fap-field" htmlFor="fap-place-search">
                  <span className="fap-field__label">Near</span>
                  <PlaceSearchInput
                    inputId="fap-place-search"
                    types={CITY_TYPES}
                    value={placeQuery}
                    onChange={setPlaceQuery}
                    onSelect={(place) => {
                      setSelectedPlace(place);
                      setPlaceQuery(place.label || place.name || '');
                    }}
                    placeholder="City, town, or county…"
                    aria-label="Search near a place"
                  />
                </label>

                <label className="fap-field" htmlFor="fap-radius">
                  <span className="fap-field__label fap-field__label--split">
                    <span>Radius</span>
                    <span>
                      {radius}
                      {' '}
                      MI
                    </span>
                  </span>
                  <input
                    id="fap-radius"
                    className="fap-radius"
                    type="range"
                    min={RADIUS_MIN}
                    max={RADIUS_MAX}
                    step={5}
                    value={radius}
                    onChange={(e) => setRadius(Number(e.target.value))}
                  />
                </label>

                <div className="fap-field">
                  <span className="fap-field__label" id="fap-showing-label">Showing</span>
                  <div className="fap-pills" role="group" aria-labelledby="fap-showing-label">
                    {SOURCE_PILLS.map((option) => {
                      const on = source === option.value;
                      return (
                        <button
                          key={option.value}
                          type="button"
                          className={`fap-pill${on ? ' is-on' : ''}`}
                          aria-pressed={on}
                          onClick={() => setSource(option.value)}
                        >
                          {option.label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {error ? <div className="fap-error" role="alert">{error}</div> : null}

                <YButton
                  type="submit"
                  variant="primary"
                  className="fap-search-submit"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      SEARCHING…
                    </>
                  ) : (
                    'SEARCH PROCESSORS'
                  )}
                </YButton>
              </form>
            </section>

            <div className="fap-add" data-testid="fap-add-card" data-add-open={addOpen ? '1' : '0'}>
              <div className="fap-add__kicker">Know one that isn&apos;t here?</div>
              <h3 className="fap-add__title">Add a processor for the rest of us</h3>
              <p className="fap-add__copy">
                Submissions go to a Yona admin for approval before they show in the directory.
              </p>
              <button
                type="button"
                className="fap-add__cta"
                aria-expanded={addOpen}
                onClick={() => openAdd()}
              >
                + ADD A PROCESSOR
              </button>
            </div>

            <div className="fap-key">
              <div className="fap-key__title">Map key</div>
              <div className="fap-key__row">
                <span className="fap-key__pin fap-key__pin--you" aria-hidden />
                <span>Search centre — the town you searched near</span>
              </div>
              <div className="fap-key__row">
                <span className="fap-key__pin fap-key__pin--yona" aria-hidden />
                <span>Yona directory — added and confirmed by members</span>
              </div>
              <div className="fap-key__row">
                <span className="fap-key__pin fap-key__pin--place" aria-hidden />
                <span>Nearby place from map data — unconfirmed</span>
              </div>
            </div>
          </div>

          <div className="fap-col">
            <div className="fap-map-frame" aria-label="Search area map">
              {selectedPlace ? (
                <>
                  <div className="fap-map-frame__badge">
                    SEARCH AREA
                    <br />
                    {radius}
                    {' '}
                    MI ·
                    {' '}
                    {placeLabel}
                  </div>
                  <ProcessorMapView
                    center={{
                      latitude: selectedPlace.latitude,
                      longitude: selectedPlace.longitude,
                    }}
                    radiusMiles={radius}
                    rows={hasSearched ? rows : []}
                    activeId={activeId}
                    onSelect={handleSelect}
                  />
                </>
              ) : (
                <div className="fap-map-frame__empty" data-testid="fap-map-empty">
                  <p>Select a city or town to preview your search radius on the map.</p>
                </div>
              )}
            </div>

            <section className="fap-results" aria-live="polite">
              <div className="fap-results__header">
                <h2 className="fap-results__title">
                  {headingCount}
                  {headingCount === 1 ? ' processor found' : ' processors found'}
                </h2>
                <span className="fap-results__meta">
                  WITHIN
                  {' '}
                  {radius}
                  {' '}
                  MI · NEAREST FIRST
                </span>
              </div>

              {showTrace ? (
                <div className="fap-trace" data-testid="fap-trace">
                  {formatProcessorTrace(trace)}
                </div>
              ) : null}

              <div className="fap-range">
                <div className="fap-range__head">
                  <span>Processors in range</span>
                  <span className="fap-range__callahead">Call ahead</span>
                </div>
                {rows.length === 0 ? (
                  <div className="fap-empty" data-testid="fap-empty">
                    <div className="fap-empty__title">{empty.title}</div>
                    <div className="fap-empty__body">{empty.body}</div>
                  </div>
                ) : (
                  rows.map((row) => (
                    <div
                      key={row.id}
                      ref={(node) => {
                        if (node) rowRefs.current[row.id] = node;
                      }}
                    >
                      <ProcessorRow
                        row={row}
                        highlighted={String(activeId) === String(row.id)}
                        onSelect={handleSelect}
                      />
                    </div>
                  ))
                )}
              </div>
            </section>
          </div>
        </div>
        {activeRow ? (
          <ProcessorDetailPanel
            row={activeRow}
            onClose={closeDetail}
            onCompleteListing={openAdd}
          />
        ) : null}
        <AddProcessorPanel open={addOpen} seed={addSeed} onClose={closeAdd} />
      </YPageShellBody>
    </YPageShell>
  );
}
